import {
  Box,
  Text,
  VStack,
  useColorModeValue,
  Heading,
  UnorderedList,
  OrderedList,
  ListItem,
  Code,
  Divider
} from '@chakra-ui/react';
import type { ContentItem } from '../../types';

export interface MarkdownContentProps {
  content: ContentItem & { data?: any };
  onComplete?: (contentId: string) => void;
}

// Simple markdown-like renderer for basic content
function SimpleMarkdownRenderer({ content }: { content: string }) {
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const codeColor = useColorModeValue('purple.600', 'purple.300');
  const bgColor = useColorModeValue('gray.100', 'gray.700');

  const lines = content.split('\n');
  const elements: JSX.Element[] = [];

  let listItems: string[] = [];
  let listType: 'ul' | 'ol' | null = null;

  const flushList = () => {
    if (listItems.length > 0) {
      const ListComponent = listType === 'ol' ? OrderedList : UnorderedList;
      elements.push(
        <ListComponent key={elements.length} spacing={2} mb={4}>
          {listItems.map((item, index) => (
            <ListItem key={index}>{item}</ListItem>
          ))}
        </ListComponent>
      );
      listItems = [];
      listType = null;
    }
  };

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      flushList();
      return;
    }

    // Headers
    if (trimmedLine.startsWith('# ')) {
      flushList();
      elements.push(
        <Heading key={index} as="h1" size="xl" mb={4} color={textColor}>
          {trimmedLine.substring(2)}
        </Heading>
      );
    } else if (trimmedLine.startsWith('## ')) {
      flushList();
      elements.push(
        <Heading key={index} as="h2" size="lg" mb={3} color={textColor}>
          {trimmedLine.substring(3)}
        </Heading>
      );
    } else if (trimmedLine.startsWith('### ')) {
      flushList();
      elements.push(
        <Heading key={index} as="h3" size="md" mb={2} color={textColor}>
          {trimmedLine.substring(4)}
        </Heading>
      );
    }
    // Unordered lists
    else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      listItems.push(trimmedLine.substring(2));
    }
    // Ordered lists
    else if (/^\d+\.\s/.test(trimmedLine)) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      listItems.push(trimmedLine.replace(/^\d+\.\s/, ''));
    }
    // Code blocks
    else if (trimmedLine.startsWith('```')) {
      flushList();
      // Handle code blocks - simplified version
      elements.push(
        <Code key={index} p={3} bg={bgColor} color={codeColor} borderRadius="md" display="block" mb={4}>
          Code block
        </Code>
      );
    }
    // Horizontal rule
    else if (trimmedLine === '---') {
      flushList();
      elements.push(<Divider key={index} my={4} />);
    }
    // Regular paragraph
    else {
      flushList();
      // Handle inline formatting
      let processedLine = trimmedLine;

      // Bold text
      processedLine = processedLine.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // Inline code
      processedLine = processedLine.replace(/`(.*?)`/g, '<code>$1</code>');

      elements.push(
        <Text
          key={index}
          mb={3}
          color={textColor}
          dangerouslySetInnerHTML={{ __html: processedLine }}
        />
      );
    }
  });

  flushList(); // Flush any remaining list items

  return <>{elements}</>;
}

export function MarkdownContent({ content }: MarkdownContentProps) {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const markdownContent = content.data?.markdown || content.data?.content || `
# ${content.title}

This is a sample markdown content. You can replace this with actual markdown content.

## Features

- Support for headers
- **Bold text** and \`inline code\`
- Lists (ordered and unordered)
- And more markdown features

## Code Example

\`\`\`javascript
console.log("Hello, world!");
\`\`\`

---

*This content can be marked as complete when you're done reading.*
  `.trim();

  return (
    <Box
      w="100%"
      h="100%"
      bg={bgColor}
      border="1px"
      borderColor={borderColor}
      borderRadius="lg"
      overflow="auto"
      p={6}
    >
      <VStack align="stretch" spacing={4}>
        <Box flex={1}>
          <SimpleMarkdownRenderer content={markdownContent} />
        </Box>
      </VStack>
    </Box>
  );
} 